<template>
  <div class="content mtr-page">
    <!-- ============================================================
         TOOLBAR CARD: Bộ lọc + Tác vụ nhanh
         ============================================================ -->
    <div class="mtr-toolbar-card row-data">
      <!-- Vùng Bộ lọc -->
      <div class="mtr-toolbar-section mtr-toolbar-section--filter">
        <div class="mtr-card-head">
          <div>
            <p class="mtr-card-title">Bộ lọc tìm kiếm</p>
            <p class="mtr-card-subtitle">
              Tìm nhanh khoản thu chính theo tên, loại, công thức và thuế.
            </p>
          </div>
        </div>

        <div class="mtr-filter-grid">
          <!-- Lọc Loại: Thu / Hoàn -->
          <el-select
            v-model="searchRequest.category"
            placeholder="Loại khoản"
            clearable
            class="mtr-filter-control"
            @change="handleSearch"
          >
            <el-option label="Thu" value="IN" />
            <el-option label="Hoàn" value="OUT" />
          </el-select>

          <!-- Lọc Có công thức -->
          <el-select
            v-model="searchRequest.hasFormula"
            placeholder="Công thức"
            clearable
            class="mtr-filter-control"
            @change="handleSearch"
          >
            <el-option label="Có công thức" :value="true" />
            <el-option label="Không có công thức" :value="false" />
          </el-select>

          <!-- Lọc Khai báo thuế -->
          <el-select
            v-model="searchRequest.taxDeclaration"
            placeholder="Khai báo thuế"
            clearable
            class="mtr-filter-control"
            @change="handleSearch"
          >
            <el-option label="Có khai báo thuế" :value="true" />
            <el-option label="Không khai báo thuế" :value="false" />
          </el-select>

          <!-- Lọc Hiển thị hoá đơn điện tử -->
          <el-select
            v-model="searchRequest.issuingElectronicInvoices"
            placeholder="Hoá đơn điện tử"
            clearable
            class="mtr-filter-control"
            @change="handleSearch"
          >
            <el-option label="Sử dụng HĐ điện tử" :value="true" />
            <el-option label="Không sử dụng HĐ điện tử" :value="false" />
          </el-select>

          <!-- Tìm theo tên -->
          <el-input
              v-model="searchRequest.keyword"
              placeholder="Nhập tên khoản thu..."
              clearable
              class="mtr-search-input"
              @keyup.enter.native="handleSearch"
              @clear="handleSearch"
          >
            <template #append>
              <el-button icon="el-icon-search" @click="handleSearch" />
            </template>
          </el-input>
        </div>
      </div>

      <!-- Đường kẻ ngăn cách -->
      <div class="mtr-toolbar-divider" />

      <!-- Vùng Tác vụ nhanh -->
      <div class="mtr-toolbar-section mtr-toolbar-section--action">
        <div class="mtr-card-head mtr-card-head--compact">
          <div>
            <p class="mtr-card-title">Tác vụ nhanh</p>
            <p class="mtr-card-subtitle">
              Thêm mới khoản thu chính vào hệ thống.
            </p>
          </div>
        </div>
        <div class="mtr-action-group">
          <el-button
            type="success"
            class="mtr-action-button mtr-action-button--primary"
            @click="openCreateDialog"
          >
            <i class="el-icon-plus" />
            Thêm mới
          </el-button>
        </div>
      </div>
    </div>

    <!-- ============================================================
         TABLE CARD: Danh sách khoản chính
         ============================================================ -->
    <div class="mtr-table-card row-data">
      <!-- Header bảng -->
      <div class="mtr-card-head mtr-table-head">
        <div>
          <p class="mtr-card-title">Danh sách khoản chính</p>
          <p class="mtr-card-subtitle">
            Quản lý toàn bộ khoản thu chính: đơn giá / công thức, thuế, giảm giá và điểm danh.
          </p>
        </div>
      </div>

      <!-- Bảng dữ liệu -->
      <div class="mtr-table-wrapper">
        <el-table
          v-loading="tableLoading"
          :data="totalRevenueList"
          border
          class="sk-table"
          highlight-current-row
          :max-height="$tableMaxHeightSmall"
        >
          <!-- STT -->
          <el-table-column
            label="STT"
            type="index"
            width="55"
            align="center"
            :index="indexMethod"
          />

          <!-- Tên khoản thu -->
          <el-table-column label="Tên khoản thu" prop="name" min-width="200" />

          <!-- Mô tả -->
          <el-table-column label="Mô tả" min-width="180">
            <template slot-scope="{ row }">
              <div>
                <div :class="['mtr-desc', { expanded: isDescriptionExpanded(row.id) }]">{{ row.description || '—' }}</div>
                <div v-if="showDescriptionToggle(row.description)" class="mtr-desc-toggle">
                  <a href="#" @click.prevent="toggleDescription(row.id)">
                    {{ isDescriptionExpanded(row.id) ? 'Thu gọn' : 'Xem thêm' }}
                  </a>
                </div>
              </div>
            </template>
          </el-table-column>

          <!-- Loại (Thu/Hoàn) -->
          <el-table-column label="Loại" width="85" align="center">
            <template slot-scope="{ row }">
              <el-tag
                :type="row.category === 'IN' ? 'success' : 'danger'"
                size="mini"
                effect="light"
              >
                {{ row.category === "IN" ? "Thu" : "Hoàn" }}
              </el-tag>
            </template>
          </el-table-column>

          <!-- Dùng công thức -->
          <el-table-column label="Công thức" width="105" align="center">
            <template slot-scope="{ row }">
              <el-tag v-if="row.hasFormula" type="warning" size="mini" effect="dark">
                <i class="el-icon-s-operation" /> Có
              </el-tag>
              <span v-else class="mtr-text-muted">—</span>
            </template>
          </el-table-column>

          <!-- Biểu thức công thức -->
          <el-table-column label="Biểu thức" min-width="140">
            <template slot-scope="{ row }">
              <span v-if="row.hasFormula && row.formulaExpression" class="mtr-formula-badge">
                {{ row.formulaExpression }}
              </span>
              <span v-else class="mtr-text-muted">—</span>
            </template>
          </el-table-column>

          <!-- Đơn giá -->
          <el-table-column label="Đơn giá" width="145" align="right">
            <template slot-scope="{ row }">
              <span v-if="!row.hasFormula" class="mtr-money">{{ formatMoney(row.price) }}</span>
              <span v-else class="mtr-text-muted">—</span>
            </template>
          </el-table-column>

          <!-- Đơn vị -->
          <el-table-column label="Đơn vị" prop="unit" width="90" align="center" />

          <!-- Số lượng -->
          <el-table-column label="SL" width="70" align="center">
            <template slot-scope="{ row }">
              {{ formatQuantity(row.quantity) }}
            </template>
          </el-table-column>

          <!-- Giảm giá -->
          <el-table-column label="Giảm giá" width="90" align="center">
            <template slot-scope="{ row }">
              <i
                :class="row.discount ? 'el-icon-circle-check' : 'el-icon-close'"
                :style="{ color: row.discount ? '#16a34a' : '#dc2626', fontSize: '18px' }"
              />
            </template>
          </el-table-column>

          <!-- Áp dụng điểm danh -->
          <el-table-column label="Điểm danh" width="100" align="center">
            <template slot-scope="{ row }">
              <i
                :class="row.attendance ? 'el-icon-circle-check' : 'el-icon-close'"
                :style="{ color: row.attendance ? '#16a34a' : '#dc2626', fontSize: '18px' }"
              />
            </template>
          </el-table-column>

          <!-- Thời gian áp dụng -->
          <el-table-column label="Chu kỳ" width="130" align="center">
            <template slot-scope="{ row }">
              <el-tag size="mini" effect="plain" type="info">
                {{ appliedTimeTypeLabel(row.appliedTimeType) }}
              </el-tag>
            </template>
          </el-table-column>

          <!-- Thao tác -->
          <el-table-column label="Thao tác" width="120" align="center" fixed="right">
            <template slot-scope="{ row }">
              <el-tooltip content="Chỉnh sửa" placement="top">
                <el-button
                  type="primary"
                  icon="el-icon-edit"
                  size="mini"
                  circle
                  plain
                  @click="openEditDialog(row)"
                />
              </el-tooltip>
              <el-tooltip content="Xóa khoản thu" placement="top">
                <el-button
                  type="danger"
                  icon="el-icon-delete"
                  size="mini"
                  circle
                  plain
                  class="ml-1"
                  @click="handleDelete(row.id)"
                />
              </el-tooltip>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <!-- Phân trang -->
      <div class="mtr-pagination">
        <el-pagination
          background
          :current-page.sync="pagination.page"
          :page-sizes="[25, 50, 100, 200]"
          :page-size.sync="pagination.size"
          :total="pagination.total"
          layout="sizes, prev, pager, next"
          @size-change="handleSizeChange"
          @current-change="handlePageChange"
        />
      </div>
    </div>

    <!-- ============================================================
         DIALOG THÊM MỚI / CẬP NHẬT
         ============================================================ -->
    <total-revenue-form-dialog ref="formDialog" @saved="fetchData" />
  </div>
</template>

<script>
import ManagingTotalRevenueService from "@/services/tutition_v3/ManagingTotalRevenueService";
import TotalRevenueFormDialog from "@/components/tutition_v3/dialog/TotalRevenueFormDialog.vue";
import NotifyMessage from "@/commonFuncion/NotifyMessage";
import { formatQuantity as formatQuantityValue } from "@/components/tutition_v3/utils/quantity";

export default {
  name: "ManagingTotalRevenue",
  components: { TotalRevenueFormDialog },

  data() {
    return {
      tableLoading: false,
      totalRevenueList: [],

      searchRequest: {
        keyword: "",
        category: "",
        hasFormula: null,
        taxDeclaration: null,
        issuingElectronicInvoices: null,
      },

      // page FE bắt đầu từ 1, BE từ 0
      pagination: { page: 1, size: 25, total: 0 },
      expandedDescriptionIds: [],
    };
  },

  created() {
    this.fetchData();
  },

  methods: {
    // ─── Helpers ──────────────────────────────────────────────────
    indexMethod(index) {
      return (this.pagination.page - 1) * this.pagination.size + index + 1;
    },

    formatMoney(value) {
      if (value === null || value === undefined || value === "") return "—";
      return Number(value).toLocaleString("vi-VN") + " ₫";
    },

    formatQuantity(value) {
      return formatQuantityValue(value);
    },

    appliedTimeTypeLabel(type) {
      return (
        { YEAR: "Theo năm", MONTH: "Theo tháng", TIME_PERIOD: "Khoảng ngày", UNIT: "Theo đơn vị" }[type] ||
        (type || "—")
      );
    },

    isDescriptionExpanded(id) {
      return this.expandedDescriptionIds.includes(id);
    },
    toggleDescription(id) {
      const idx = this.expandedDescriptionIds.indexOf(id);
      if (idx === -1) this.expandedDescriptionIds.push(id);
      else this.expandedDescriptionIds.splice(idx, 1);
    },
    showDescriptionToggle(desc) {
      return desc && String(desc).length > 120;
    },

    // ─── API ──────────────────────────────────────────────────────

    /**
     * Tải Danh sách khoản chính.
     * page FE (1-based) → trừ 1 trước khi gửi lên BE (0-based).
     */
    async fetchData() {
      this.tableLoading = true;
      try {
        const params = {
          ...this.searchRequest,
          page: this.pagination.page - 1,
          size: this.pagination.size,
        };
        // Loại bỏ key rỗng / null / undefined
        Object.keys(params).forEach(k => {
          if (params[k] === "" || params[k] === null || params[k] === undefined) delete params[k];
        });

        const res = await ManagingTotalRevenueService.search(params);
        this.totalRevenueList = res.data.data;
        this.pagination.total = res.data.total;
      } catch (err) {
        NotifyMessage.leftCornerNotificationError(
          err?.response?.data?.message || "Không thể tải Danh sách khoản chính!"
        );
      } finally {
        this.tableLoading = false;
      }
    },

    handleSearch() {
      this.pagination.page = 1;
      this.fetchData();
    },

    handleSizeChange(size) {
      this.pagination.size = size;
      this.pagination.page = 1;
      this.fetchData();
    },

    handlePageChange(page) {
      this.pagination.page = page;
      this.fetchData();
    },

    /**
     * Xóa mềm – hiển thị $confirm trước khi gọi API DELETE.
     */
    async handleDelete(id) {
      try {
        await this.$confirm(
          "Bạn có chắc chắn muốn xóa khoản thu chính này? Các học sinh đăng ký khoản này cũng sẽ xoá khỏi danh sách đăng ký khoản. Thao tác không thể hoàn tác.",
          "Xác nhận xóa",
          { confirmButtonText: "Xóa", cancelButtonText: "Hủy", type: "warning" }
        );
      } catch { return; }

      try {
        const res = await ManagingTotalRevenueService.remove(id);
        NotifyMessage.leftCornerNotificationSuccess(res.data.message || "Xóa khoản thu chính thành công!");
        this.fetchData();
      } catch (err) {
        NotifyMessage.leftCornerNotificationError(
          err?.response?.data?.message || "Xóa khoản thu chính thất bại!"
        );
      }
    },

    // ─── Mở dialog ────────────────────────────────────────────────
    openCreateDialog() {
      this.$refs.formDialog.openForCreate();
    },

    openEditDialog(row) {
      this.$refs.formDialog.openForEdit(row.id);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/style/sk-table.scss";

/* ===================================================
   Trang quản lý khoản thu chính – MTR Page Layout
   =================================================== */

.mtr-page {
  padding: 4px 0 16px;

  .row-data {
    margin-bottom: 20px;
  }
}

/* ─── Toolbar card ─────────────────────────────────── */
.mtr-toolbar-card {
  display: flex;
  align-items: flex-start;
  gap: 18px;
  padding: 18px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.05);
}

.mtr-toolbar-section { min-width: 0; }
.mtr-toolbar-section--filter  { flex: 2; }
.mtr-toolbar-section--action  { flex: 0 0 auto; display: flex; flex-direction: column; justify-content: space-between; }

.mtr-toolbar-divider {
  width: 1px;
  align-self: stretch;
  background: #e2e8f0;
  flex-shrink: 0;
  margin: 0 4px;
}

/* ─── Table card ───────────────────────────────────── */
.mtr-table-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.05);
  padding: 18px 18px 14px;
}

/* ─── Card head ────────────────────────────────────── */
.mtr-card-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}
.mtr-card-head--compact { margin-bottom: 14px; }
.mtr-table-head { align-items: center; }

.mtr-card-title {
  margin: 0 0 4px;
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
}
.mtr-card-subtitle {
  margin: 0;
  font-size: 12.5px;
  line-height: 1.5;
  color: #64748b;
}

/* ─── Bộ lọc ──────────────────────────────────────── */
.mtr-filter-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}
.mtr-search-input  { width: 280px; }
.mtr-filter-control { width: 185px; }

/* ─── Tác vụ nhanh ────────────────────────────────── */
.mtr-action-group {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.mtr-action-button { min-width: 130px; font-weight: 600; }
.mtr-action-button--primary { box-shadow: 0 6px 16px rgba(34, 197, 94, 0.2); }

/* ─── Bảng ────────────────────────────────────────── */
.mtr-table-wrapper {
  border-radius: 14px;
  overflow: hidden;
}

.mtr-money {
  font-variant-numeric: tabular-nums;
  color: #475569;
  font-size: 13px;
}

.mtr-text-muted { color: #94a3b8; font-size: 13px; }

.mtr-formula-badge {
  display: inline-block;
  padding: 2px 8px;
  background: #fef3c7;
  border: 1px solid #fcd34d;
  border-radius: 4px;
  font-size: 12px;
  font-family: monospace;
  color: #92400e;
  word-break: break-all;
}

/* Tiêu đề cột bảng: nền xanh đậm */
/deep/ .sk-table thead th,
/deep/ .sk-table thead td {
  background: #2563eb !important;
  color: #ffffff !important;
  font-weight: 700;
  border-bottom: 1px solid rgba(255,255,255,0.08);
}
/deep/ .sk-table thead th > .cell,
/deep/ .sk-table thead td > .cell {
  color: #ffffff !important;
  font-weight: 700;
}
/deep/ .sk-table thead tr { height: 56px; }
/deep/ .sk-table tbody td { background: #ffffff; }

/* ─── Phân trang ──────────────────────────────────── */
.mtr-pagination {
  display: flex;
  justify-content: center;
  margin-top: 16px;
}

/* ─── Input / Button bo góc ─────────────────────────── */
/deep/ .mtr-page .el-input__inner,
/deep/ .mtr-page .el-select .el-input__inner {
  border-radius: 10px;
}
/deep/ .mtr-page .el-button { border-radius: 10px; }
/deep/ .mtr-page .el-input-group__append .el-button {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}

/* ─── Responsive ──────────────────────────────────── */
@media (max-width: 1200px) {
  .mtr-toolbar-card {
    flex-direction: column;
    align-items: stretch;
  }
  .mtr-toolbar-divider {
    width: 100%;
    height: 1px;
    margin: 4px 0;
  }
  .mtr-table-head {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
}

@media (max-width: 768px) {
  .mtr-filter-grid,
  .mtr-action-group {
    flex-direction: column;
    align-items: stretch;
  }
  .mtr-search-input,
  .mtr-filter-control,
  .mtr-action-button {
    width: 100%;
  }
}

/* Mô tả: clamp 2 dòng, show toggle */
.mtr-desc {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;
}
.mtr-desc.expanded {
  -webkit-line-clamp: unset;
  overflow: visible;
}
.mtr-desc-toggle { margin-top: 6px; }
.mtr-desc-toggle a { color: #2563eb; cursor: pointer; font-size: 13px; }
</style>
